// Trocar de plano ou mudar para o anual, para quem já assina: resumo, quando o plano novo começa e a diferença a pagar.
// Quem não assina vai direto de Planos para o Checkout da Stripe.
import { useEffect, useState } from 'react';
import {
  type Ciclo, DIAS_PARA_PAGAR_PIX, MESES_GRATIS_NO_ANUAL, NOME_DO_PLANO, PRECO_CENTAVOS, type PlanoPago,
  TENTOS_POR_MES, economiaNoAnual, emReais, liberouAgora, ordemDoPlano, parcelaDoAnual, precoEmReais,
} from '@truco6/planos';
import { type ResumoAssinatura, api, dataCompleta, dataDoCiclo, daquiAUmAno } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { compra, medir } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { AbasCiclo, Icone, Moeda, SeloEconomia, useEu } from './planos-comum.tsx';
import { Tento } from './tentos-comum.tsx';
import { Carregando, Tela, Topo } from './comum.tsx';

function Resumo({ plano, ciclo, sub }: { plano: PlanoPago; ciclo: Ciclo; sub: string }) {
  const novos = liberouAgora(plano);
  return (
    <section className="cartao plano-resumo" aria-label="Resumo">
      <div className="plano-cab">
        <Moeda plano={plano} tamanho={56} />
        <div>
          <h2>Plano {NOME_DO_PLANO[plano]}</h2>
          <p className="nota">{sub}</p>
        </div>
      </div>
      {novos.length > 0 && <ul className="chips">{novos.map((i) => <li key={i.recurso}>{i.texto}</li>)}</ul>}
      {ciclo === 'mensal' ? (
        <>
          <div className="linha-valor"><span>{NOME_DO_PLANO[plano]} mensal</span><span>{precoEmReais(plano)}</span></div>
          <div className="linha-total"><span>Total</span><b>{precoEmReais(plano)}/mês</b></div>
        </>
      ) : (
        <>
          <div className="linha-valor"><span>12 meses no mensal</span><s className="riscado">{emReais(12 * PRECO_CENTAVOS[plano])}</s></div>
          <div className="linha-economia"><span>Economia de {MESES_GRATIS_NO_ANUAL} meses</span><b>− {emReais(economiaNoAnual(plano))}</b></div>
          <div className="linha-total"><span>Total</span><b>{precoEmReais(plano, 'anual')}/ano</b></div>
          <p className="equivale"><SeloEconomia /><span>Equivale a <b>{emReais(parcelaDoAnual(plano))}/mês</b>, à vista uma vez por ano.</span></p>
        </>
      )}
    </section>
  );
}

function Troca({ sessao, atual, plano, ciclo }: { sessao: Sessao; atual: ResumoAssinatura; plano: PlanoPago; ciclo: Ciclo }) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState<number | null>(null);
  const [pix, setPix] = useState<string | null>(null);
  const novoCiclo = atual.ciclo !== ciclo;
  const sobe = ordemDoPlano(plano) > ordemDoPlano(atual.plano);
  const cartao = atual.metodo === 'cartao';
  const agora = novoCiclo || (sobe && (cartao || atual.ciclo === 'anual'));
  const fim = atual.fimDoPeriodo ? dataDoCiclo(atual.fimDoPeriodo, atual.ciclo) : 'a próxima fatura';
  const nome = NOME_DO_PLANO[plano], porCiclo = ciclo === 'anual' ? 'por ano' : 'por mês';
  // Subida na hora de um plano com Tentos (Prata para Ouro): a diferença do mês entra quando a diferença é paga.
  const tentosDaSubida = agora && sobe && TENTOS_POR_MES[atual.plano] > 0 ? TENTOS_POR_MES[plano] - TENTOS_POR_MES[atual.plano] : 0;

  const voltarAoPlano = (texto: string, nota?: string) => (
    <>
      <p className="status">{texto}</p>
      {nota && <p className="nota">{nota}</p>}
      <button className="btn2 largo" onClick={() => ir({ tela: 'meu-plano' })}>Ver meu plano</button>
    </>
  );
  if (atual.plano === plano && !novoCiclo) return voltarAoPlano(`Você já está no ${nome} ${ciclo}.`);
  if (atual.ciclo === 'anual' && ciclo === 'mensal') {
    return voltarAoPlano(`Seu plano é anual até ${fim}.`, 'Para voltar ao mensal, cancele a renovação em Meu plano e assine o mensal quando o ano acabar.');
  }
  if (atual.status === 'cancelada') {
    return voltarAoPlano(`Sua assinatura do ${NOME_DO_PLANO[atual.plano]} está cancelada.`, 'Para trocar de plano, mantenha a assinatura em Meu plano.');
  }

  let info: React.ReactNode;
  if (novoCiclo && cartao) {
    info = <div><Icone nome="cartao" /><p><b>Hoje, o ano menos o que sobrou do mês</b>
      <span>Você paga hoje {precoEmReais(plano, 'anual')}, descontando o que já pagou e não usou do mês. Depois, renova em {dataCompleta(daquiAUmAno(Date.now()))}.</span></p></div>;
  } else if (novoCiclo) {
    info = <div><Icone nome="pix" /><p><b>Fatura Pix do ano</b>
      <span>Chega agora a fatura do ano ({precoEmReais(plano, 'anual')}), com desconto do que você já pagou e não usou do mês. Pague em até {DIAS_PARA_PAGAR_PIX} dias. Até pagar, segue o mensal.</span></p></div>;
  } else if (agora && cartao) {
    info = <div><Icone nome="cartao" /><p><b>Hoje, só a diferença</b>
      <span>Você paga hoje a diferença proporcional até {fim}. Depois, {precoEmReais(plano, ciclo)} {porCiclo} no cartão.</span></p></div>;
  } else if (agora) {
    info = <div><Icone nome="pix" /><p><b>Fatura Pix da diferença</b>
      <span>Chega agora uma fatura com a diferença proporcional até {fim}. Pague em até {DIAS_PARA_PAGAR_PIX} dias. Até pagar, segue o {NOME_DO_PLANO[atual.plano]}.</span></p></div>;
  } else {
    info = <div><Icone nome="calendario" /><p><b>O {nome} começa na próxima fatura, em {fim}.</b>
      <span>Até lá, segue o {NOME_DO_PLANO[atual.plano]}.</span></p></div>;
  }

  let fecho: React.ReactNode;
  if (pix) {
    fecho = (
      <div className="rodape-cta">
        <p className="ok" role="status">Pronto. Pague o Pix em até {DIAS_PARA_PAGAR_PIX} dias para valer o {nome} {ciclo}.</p>
        <a className="btn1 largo botao-link" href={pix} target="_blank" rel="noopener">PAGAR COM PIX</a>
        <button className="link" onClick={() => ir({ tela: 'meu-plano' }, true)}>Ver meu plano</button>
      </div>
    );
  } else if (pronto !== null) {
    fecho = (
      <>
        <p className="ok" role="status">Pronto. O {nome} começa em {dataDoCiclo(pronto, ciclo)}.</p>
        <button className="btn2 largo" onClick={() => ir({ tela: 'meu-plano' }, true)}>Ver meu plano</button>
      </>
    );
  } else {
    fecho = (
      <div className="rodape-cta">
        <button className="btn1 largo" disabled={ocupado} onClick={async () => {
          setErro(null); setOcupado(true);
          try {
            const r = await api<{ url: string | null; aPartirDe: number | null; pix: boolean }>(sessao, 'POST', '/assinatura/trocar', { plano, ciclo });
            medir('trocar_plano', compra(plano, ciclo, { de: `${atual.plano}_${atual.ciclo}`, payment_type: atual.metodo, agora: !r.aPartirDe }));
            if (r.pix && r.url) { setPix(r.url); setOcupado(false); return; }
            if (r.url) { location.assign(r.url); return; } // o banco pediu confirmação do cartão
            if (r.aPartirDe) { setPronto(r.aPartirDe); setOcupado(false); return; }
            ir({ tela: 'assinatura-ok', plano }, true);
          } catch (e) { setErro((e as Error).message); setOcupado(false); }
        }}>{ocupado ? 'UM INSTANTE...' : novoCiclo ? 'MUDAR PARA O ANUAL' : 'CONFIRMAR TROCA'}</button>
      </div>
    );
  }

  return (
    <div className="duas-colunas">
      <div className="col">
        <Resumo plano={plano} ciclo={ciclo} sub={`Hoje você está no ${NOME_DO_PLANO[atual.plano]} ${atual.ciclo}`} />
      </div>
      <div className="col">
        <section className="cartao lista-info">{info}</section>
        {tentosDaSubida > 0 && (
          <p className="nota com-icone"><Tento tamanho={18} vinco={false} />
            <span>Com a diferença paga, você recebe na hora {tentosDaSubida} Tentos, a diferença do mês.</span></p>
        )}
        {erro && <p className="erro" role="alert">{erro}</p>}
        {fecho}
      </div>
    </div>
  );
}

// /planos/assinar?plano=prata&ciclo=anual (os botões do site e Meu plano): quem já assina troca de plano aqui; quem
// não assina volta para Planos com o plano e o ciclo marcados, e o toque em ASSINAR leva direto à Stripe.
export function Assinar({ sessao, plano, ciclo }: { sessao: Sessao; plano: PlanoPago; ciclo: Ciclo }) {
  const { eu, erro: erroEu } = useEu(sessao);
  const atual = eu?.assinatura && eu.assinatura.status !== 'aguardando_pix' ? eu.assinatura : null;
  const semAssinatura = (!!eu || !!erroEu) && !atual;
  useEffect(() => { if (semAssinatura) ir({ tela: 'planos', plano, ciclo }, true); }, [semAssinatura, plano, ciclo]);
  const mudarCiclo = (c: Ciclo) => ir({ tela: 'assinar', plano, ciclo: c }, true);

  if (!atual) return <Carregando />;
  const titulo = atual.ciclo === 'mensal' && ciclo === 'anual' ? 'Mudar para o anual' : 'Trocar de plano';
  return (
    <Tela>
      <Topo titulo={titulo} voltar={voltar} />
      {atual.ciclo === 'mensal' && <AbasCiclo ciclo={ciclo} aoMudar={mudarCiclo} />}
      <Troca sessao={sessao} atual={atual} plano={plano} ciclo={ciclo} />
    </Tela>
  );
}
